import { createContext, useContext, useState, useCallback, ReactNode, ComponentType } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ToastProps } from '../components/Toast';

export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

export interface ShowToastOptions {
  duration?: number;
  icon?: ComponentType<{ className?: string }>;
  position?: ToastPosition;
}

export interface ToastContextValue {
  toasts: ToastProps[];
  addToast: (toast: Omit<ToastProps, 'id' | 'onClose'>) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
  showToast: (message: string, options?: ShowToastOptions) => string;
  getToastsByPosition: () => Record<ToastPosition, ToastProps[]>;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<ToastProps, 'id' | 'onClose'>) => {
      const id = uuidv4();
      const newToast: ToastProps = {
        ...toast,
        id,
        onClose: removeToast,
      };

      setToasts((prev) => [...prev, newToast]);
      return id;
    },
    [removeToast]
  );

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const showToast = useCallback(
    (message: string, options?: ShowToastOptions) => {
      return addToast({
        message,
        duration: options?.duration,
        icon: options?.icon,
        position: options?.position,
      });
    },
    [addToast]
  );

  const getToastsByPosition = useCallback(() => {
    const grouped: Record<ToastPosition, ToastProps[]> = {
      'top-right': [],
      'top-left': [],
      'bottom-right': [],
      'bottom-left': [],
      'top-center': [],
      'bottom-center': [],
    };

    toasts.forEach((toast) => {
      const position = toast.position || 'bottom-center';
      grouped[position].push(toast);
    });

    return grouped;
  }, [toasts]);

  const value: ToastContextValue = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    showToast,
    getToastsByPosition,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
